import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Offcanvas } from 'react-bootstrap'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { URL as BACKEND_URL } from '../../gql/client'
import { useGlobalWebSocket } from '../../providers/WebSocketProvider'
import { useLatency } from '../../hooks/useLatency'
import { useGetUser } from '../../store/userStore'
import { useCurrentLocation } from '../../hooks/useCurrentLocation'

interface VicidialHealth {
  ok: boolean
  rtt_ms: number | null
  jitter_ms: number | null
  min_ms: number | null
  max_ms: number | null
  error?: string
}

interface SystemHealth {
  broker_ok: boolean
  queue_depth: number | null
  active_workers: number
  active_tasks: number
}

type Status = 'good' | 'warn' | 'error' | 'loading' | 'idle'

const STATUS_COLOR: Record<Status, string> = {
  good: '#50cd89',
  warn: '#ffc700',
  error: '#f1416c',
  loading: '#009ef7',
  idle: '#b5b5c3',
}

const M = defineMessages({
  title: {id: 'MENU.settings.systemMonitor', defaultMessage: 'System Monitor'},
  measuring: {id: 'admin.systemMonitor.drawer.measuring', defaultMessage: 'measuring…'},
  nextIn: {id: 'admin.systemMonitor.drawer.nextIn', defaultMessage: 'next {seconds}s'},
  start: {id: 'admin.systemMonitor.drawer.start', defaultMessage: 'Start'},
  stop: {id: 'admin.systemMonitor.drawer.stop', defaultMessage: 'Stop'},
  yourConnection: {
    id: 'admin.systemMonitor.drawer.yourConnection',
    defaultMessage: 'Your connection',
  },
  server: {id: 'admin.systemMonitor.drawer.server', defaultMessage: 'Server'},

  statQueued: {id: 'admin.systemMonitor.drawer.stat.queued', defaultMessage: 'queued'},
  statWorkers: {id: 'admin.systemMonitor.drawer.stat.workers', defaultMessage: 'workers'},
  statActive: {id: 'admin.systemMonitor.drawer.stat.active', defaultMessage: 'active'},

  jitterReference: {
    id: 'admin.systemMonitor.drawer.jitterReference',
    defaultMessage: 'Jitter reference (VoIP)',
  },
  jitterExcellent: {
    id: 'admin.systemMonitor.drawer.jitter.excellent',
    defaultMessage: 'Excellent',
  },
  jitterAcceptable: {
    id: 'admin.systemMonitor.drawer.jitter.acceptable',
    defaultMessage: 'Acceptable',
  },
  jitterBad: {
    id: 'admin.systemMonitor.drawer.jitter.bad',
    defaultMessage: 'Causes problems on calls',
  },

  currentLocation: {
    id: 'admin.systemMonitor.drawer.currentLocation',
    defaultMessage: 'Current location',
  },
  outsideUs: {id: 'admin.systemMonitor.drawer.outsideUs', defaultMessage: 'Outside U.S.'},

  pressToStart: {
    id: 'admin.systemMonitor.drawer.pressToStart',
    defaultMessage: 'Press <b>{action}</b> to start measuring',
  },

  // Compartidas con la pagina
  statusConnected: {id: 'admin.systemMonitor.status.connected', defaultMessage: 'CONNECTED'},
  statusError: {id: 'admin.systemMonitor.status.error', defaultMessage: 'ERROR'},
})

// i18n-ok-start: nombres tecnicos, simbolos y rangos numericos. Iguales en EN y
// ES; izados a const porque el rule marca cualquier literal en JSX (§17.3).
const CHANNEL = {
  webSocket: 'WebSocket',
  http: 'HTTP / REST',
  vicidial: 'Vicidial API',
  broker: 'Celery / Valkey',
}
const METRIC = {jitter: 'jitter', min: 'min', max: 'max'}
const GLYPH = {play: '▶', stop: '⏹'}
const JITTER_RANGE = {low: '< 10ms', mid: '10–30ms', high: '> 30ms'}
const DASH = '—'
const UNIT_MS = 'ms'
// i18n-ok-end

const ms = (value: number) => `${value}${UNIT_MS}`

const WS_THRESHOLDS = { good: 100, warn: 300 }
const HTTP_THRESHOLDS = { good: 150, warn: 400 }
const VICIDIAL_THRESHOLDS = { good: 200, warn: 500 }
const AUTO_REFRESH_SEC = 30

function getRttStatus(rtt: number | null, thresholds: { good: number; warn: number }): Status {
  if (rtt === null) return 'idle'
  if (rtt < thresholds.good) return 'good'
  if (rtt < thresholds.warn) return 'warn'
  return 'error'
}

const jitterColor = (j: number | null) =>
  j === null ? '#b5b5c3' : j < 10 ? '#50cd89' : j < 30 ? '#ffc700' : '#f1416c'

const mono: React.CSSProperties = { fontFamily: "'SF Mono','Fira Code','Consolas',monospace" }
const sectionLabel: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 800,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#b5b5c3',
  padding: '14px 0 4px',
}

function MetricRow({
  label,
  rtt,
  jitter,
  min,
  max,
  status,
}: {
  label: string
  rtt: number | null
  jitter: number | null
  min: number | null
  max: number | null
  status: Status
}) {
  const color = STATUS_COLOR[status]
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid #f1f1f2' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: color,
              flexShrink: 0,
              transition: 'background-color 0.3s',
            }}
          />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#3f4254' }}>{label}</span>
        </div>
        <span style={{ ...mono, fontSize: 22, fontWeight: 700, color: '#1c2333' }}>
          {rtt === null ? DASH : rtt}
          {rtt !== null && (
            <span style={{ fontSize: 11, color: '#b5b5c3', marginLeft: 2 }}>{UNIT_MS}</span>
          )}
        </span>
      </div>

      {rtt !== null && (
        <div style={{ display: 'flex', gap: 16, paddingLeft: 16, marginTop: 5 }}>
          <span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#b5b5c3', marginRight: 3 }}>{METRIC.jitter}</span>
            <span style={{ ...mono, fontSize: 11, color: jitterColor(jitter), fontWeight: 700 }}>
              {jitter === null ? DASH : ms(jitter)}
            </span>
          </span>
          <span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#b5b5c3', marginRight: 3 }}>{METRIC.min}</span>
            <span style={{ ...mono, fontSize: 11, color: '#5e6278' }}>{min === null ? DASH : ms(min)}</span>
          </span>
          <span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#b5b5c3', marginRight: 3 }}>{METRIC.max}</span>
            <span style={{ ...mono, fontSize: 11, color: '#5e6278' }}>{max === null ? DASH : ms(max)}</span>
          </span>
        </div>
      )}
    </div>
  )
}

interface Props {
  isOpen: boolean
  onClose: () => void
}

export const SystemMonitorDrawer = ({ isOpen, onClose }: Props) => {
  const intl = useIntl()
  const socket = useGlobalWebSocket()
  const { result: latency, measure } = useLatency(socket)
  const { isUsLocation, currentLocation } = useCurrentLocation()
  const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')

  const [running, setRunning] = useState(false)
  const [vicidial, setVicidial] = useState<VicidialHealth | null>(null)
  const [system, setSystem] = useState<SystemHealth | null>(null)
  const [serverLoading, setServerLoading] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SEC)
  const countdownRef = useRef<number | null>(null)

  const fetchServerHealth = useCallback(async () => {
    if (!isSuperAdmin) return
    setServerLoading(true)
    try {
      const [vRes, sRes] = await Promise.allSettled([
        fetch(`${BACKEND_URL}/health/vicidial?samples=5`).then((r) => r.json()),
        fetch(`${BACKEND_URL}/health/system`).then((r) => r.json()),
      ])
      if (vRes.status === 'fulfilled') setVicidial(vRes.value as VicidialHealth)
      if (sRes.status === 'fulfilled') setSystem(sRes.value as SystemHealth)
      setLastUpdated(new Date())
    } finally {
      setServerLoading(false)
    }
  }, [isSuperAdmin])

  const runAll = useCallback(async () => {
    setCountdown(AUTO_REFRESH_SEC)
    await Promise.all([measure(), fetchServerHealth()])
  }, [measure, fetchServerHealth])

  // Auto-start on open, auto-stop on close
  useEffect(() => {
    if (isOpen) {
      setRunning(true)
    } else {
      setRunning(false)
    }
  }, [isOpen])

  // Measurements: only when running
  useEffect(() => {
    if (!running) return
    runAll()
    const interval = window.setInterval(runAll, AUTO_REFRESH_SEC * 1000)
    return () => window.clearInterval(interval)
  }, [running]) // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown ticker
  useEffect(() => {
    if (!running) {
      if (countdownRef.current) window.clearInterval(countdownRef.current)
      return
    }
    countdownRef.current = window.setInterval(() => {
      setCountdown((c) => (c <= 1 ? AUTO_REFRESH_SEC : c - 1))
    }, 1000)
    return () => {
      if (countdownRef.current) window.clearInterval(countdownRef.current)
    }
  }, [running])

  const isLoading = latency.measuring || serverLoading
  // El glifo es puntuacion y la palabra es texto: se arman juntos fuera del JSX.
  const startLabel = `${GLYPH.play} ${intl.formatMessage(M.start)}`
  const toggleLabel = running
    ? `${GLYPH.stop} ${intl.formatMessage(M.stop)}`
    : startLabel
  const wsStatus: Status = latency.measuring ? 'loading' : getRttStatus(latency.wsRtt, WS_THRESHOLDS)
  const httpStatus: Status = latency.measuring ? 'loading' : getRttStatus(latency.httpRtt, HTTP_THRESHOLDS)
  const vicidialStatus: Status = serverLoading ? 'loading' : !vicidial ? 'idle' : !vicidial.ok ? 'error' : getRttStatus(vicidial.rtt_ms, VICIDIAL_THRESHOLDS)

  return (
    <Offcanvas show={isOpen} onHide={onClose} placement='end' style={{ width: 420 }}>
      <Offcanvas.Header closeButton style={{ borderBottom: '1px solid #f1f1f2' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', paddingRight: 8 }}>
          <div style={{ flex: 1 }}>
            <Offcanvas.Title style={{ fontSize: 15, fontWeight: 700, marginBottom: 0 }}>
              {intl.formatMessage(M.title)}
            </Offcanvas.Title>
            {lastUpdated && (
              <div style={{ ...mono, fontSize: 10, color: '#b5b5c3', marginTop: 1 }}>
                {lastUpdated.toLocaleTimeString('es', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {running && (
              <span
                style={{
                  ...mono,
                  fontSize: 10,
                  color: '#009ef7',
                  background: '#f1faff',
                  padding: '2px 8px',
                  borderRadius: 20,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {isLoading
                  ? intl.formatMessage(M.measuring)
                  : intl.formatMessage(M.nextIn, {seconds: countdown})}
              </span>
            )}
            <Button
              size='sm'
              variant={running ? 'light' : 'primary'}
              style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', whiteSpace: 'nowrap' }}
              onClick={() => setRunning((r) => !r)}
            >
              {toggleLabel}
            </Button>
          </div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ padding: '0 20px 20px' }}>
        {/* Tu conexión */}
        <div style={sectionLabel}>{intl.formatMessage(M.yourConnection)}</div>

        <MetricRow
          label={CHANNEL.webSocket}
          rtt={latency.wsRtt}
          jitter={latency.wsJitter}
          min={latency.wsMin}
          max={latency.wsMax}
          status={wsStatus}
        />
        <MetricRow
          label={CHANNEL.http}
          rtt={latency.httpRtt}
          jitter={latency.httpJitter}
          min={latency.httpMin}
          max={latency.httpMax}
          status={httpStatus}
        />

        {/* Servidor (solo SuperAdministrador) */}
        {isSuperAdmin && (
          <>
            <div style={sectionLabel}>{intl.formatMessage(M.server)}</div>

            <MetricRow
              label={CHANNEL.vicidial}
              rtt={vicidial?.rtt_ms ?? null}
              jitter={vicidial?.jitter_ms ?? null}
              min={vicidial?.min_ms ?? null}
              max={vicidial?.max_ms ?? null}
              status={vicidialStatus}
            />

            {system && (
              <div style={{ padding: '10px 0', borderBottom: '1px solid #f1f1f2' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: system.broker_ok ? '#50cd89' : '#f1416c',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#3f4254' }}>
                      {CHANNEL.broker}
                    </span>
                  </div>
                  <span
                    style={{
                      ...mono,
                      fontSize: 12,
                      fontWeight: 700,
                      color: system.broker_ok ? '#50cd89' : '#f1416c',
                    }}
                  >
                    {intl.formatMessage(system.broker_ok ? M.statusConnected : M.statusError)}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 20, paddingLeft: 16, marginTop: 6 }}>
                  {[
                    { id: 'queued', value: system.queue_depth ?? DASH, label: intl.formatMessage(M.statQueued), color: (system.queue_depth ?? 0) >= 10 ? '#ffc700' : '#50cd89' },
                    { id: 'workers', value: system.active_workers, label: intl.formatMessage(M.statWorkers), color: system.active_workers > 0 ? '#50cd89' : '#f1416c' },
                    { id: 'active', value: system.active_tasks, label: intl.formatMessage(M.statActive), color: '#3f4254' },
                  ].map(({ id, value, label, color }) => (
                    <span key={id}>
                      <span style={{ ...mono, fontSize: 14, fontWeight: 700, color, marginRight: 4 }}>{value}</span>
                      <span style={{ fontSize: 11, color: '#b5b5c3' }}>{label}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Referencia de jitter */}
        <div
          style={{
            marginTop: 20,
            padding: '12px 14px',
            background: '#f9f9f9',
            borderRadius: 8,
          }}
        >
          <div style={{ ...sectionLabel, padding: '0 0 8px' }}>
            {intl.formatMessage(M.jitterReference)}
          </div>
          {(
            [
              [JITTER_RANGE.low, '#50cd89', M.jitterExcellent],
              [JITTER_RANGE.mid, '#ffc700', M.jitterAcceptable],
              [JITTER_RANGE.high, '#f1416c', M.jitterBad],
            ] as const
          ).map(([range, color, label]) => (
            <div key={range} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
              <span style={{ ...mono, fontSize: 11, color: '#5e6278', minWidth: 50 }}>{range}</span>
              <span style={{ fontSize: 11, color: '#b5b5c3' }}>
                {intl.formatMessage(label)}
              </span>
            </div>
          ))}
        </div>

        {/* Ubicación actual */}
        <div
          style={{
            marginTop: 14,
            padding: '12px 14px',
            background: isUsLocation ? '#f9f9f9' : '#fff5f5',
            border: isUsLocation ? 'none' : '1px solid #f1416c',
            borderRadius: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={sectionLabel}>{intl.formatMessage(M.currentLocation)}</span>
            {!isUsLocation && (
              <span
                className='location-alert-text'
                style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}
              >
                {intl.formatMessage(M.outsideUs)}
              </span>
            )}
          </div>
          <div
            className={!isUsLocation ? 'location-alert-text' : undefined}
            style={{ ...mono, fontSize: 14, fontWeight: 700, color: isUsLocation ? '#3f4254' : undefined }}
          >
            {currentLocation}
          </div>
        </div>

        {latency.error && (
          <div
            style={{
              marginTop: 12,
              padding: '8px 12px',
              background: '#fff5f8',
              borderLeft: '3px solid #f1416c',
              borderRadius: 4,
              fontSize: 12,
              color: '#3f4254',
            }}
          >
            {latency.error}
          </div>
        )}

        {!running && (
          <div style={{ textAlign: 'center', marginTop: 32, color: '#b5b5c3', fontSize: 13 }}>
            <FormattedMessage
              {...M.pressToStart}
              values={{b: (chunks) => <strong>{chunks}</strong>, action: startLabel}}
            />
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  )
}
